// components/BulkUploadModalContent.tsx
import React, { useState, useRef } from "react";
import {
  List,
  ListItem,
  Box,
  Typography,
  Button,
  LinearProgress,
  ListItemSecondaryAction,
  Grid,
  useTheme,
  useMediaQuery,
  Modal,
  Skeleton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";
import { RootState } from "../../services/redux/store";
import { useDispatch, useSelector } from "react-redux";
import PDFViewer from "../../components/ui/PDFViewer";
import Close from "@mui/icons-material/Close";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";
import uploadIcon from "../../assets/icons/upload.png";
import { TruncatedTextWithTooltip } from "../../components/ui/TruncatedTextWithTooltip";
import { ALLOWED_FILE_TYPES, ALLOWED_EXTENSIONS, MAX_FILE_SIZE, FILE_INPUT_ACCEPT } from "./ApplicantTabs/constants";
import { validateImageFile, ImageType } from "../../utils/imageValidationUtils";

export interface UploadedFile {
  id: string;
  name: string;
  progress: number;
  status: "uploading" | "uploaded" | "error";
  base64?: string;
  file?: File;
}
interface BulkUploadModalContentProps {
  onCancel: () => void;
  onContinue: (files: UploadedFile[]) => void;
  tabIndex: number;
  handleDocCount: (submissionTrackingId: any) => void;
  setSnackbar: (snackbar: { open: boolean; message: string; severity: "error" | "success" }) => void;
  partnerStartedJourney: boolean;
}

const BulkUploadModalContent: React.FC<BulkUploadModalContentProps> = ({
  onCancel,
  onContinue,
  tabIndex,
  handleDocCount,
  setSnackbar,
  partnerStartedJourney,
}) => {
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [viewDocUrl, setViewDocUrl] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const submissionTrackingId: any = useSelector(
    (state: RootState) => state.stepper.trackingId
  );

  // Removed the progress simulation useEffect since we handle conversion directly
  const handleDropzoneClick = () => {
    fileInputRef.current?.click();
  };

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix (e.g., "data:image/png;base64,")
        const base64 = result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      const processFiles = async () => {
        const newFiles: UploadedFile[] = [];
        const invalidFiles: string[] = [];

        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          const fileId = `file-${Date.now()}-${i}`;

          // Check file size first
          if (file.size > MAX_FILE_SIZE) {
            invalidFiles.push(`${file.name} (exceeds 10MB)`);
            continue;
          }

          // Check if file is empty
          if (file.size === 0) {
            invalidFiles.push(`${file.name} (empty file)`);
            continue;
          }

          const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
          const isPdf = fileExtension === "pdf";

          if (isPdf) {
            // For PDF, use MIME type validation (PDF magic bytes validation can be added)
            const fileType = (file.type || "").toLowerCase();
            if (fileType !== "application/pdf" && fileExtension !== "pdf") {
              invalidFiles.push(`${file.name} (invalid PDF)`);
              continue;
            }

            // Create file object for PDF
            const newFile: UploadedFile = {
              id: fileId,
              name: file.name,
              progress: 0,
              status: "uploading" as const,
              file: file,
            };

            // Convert PDF to base64
            try {
              const base64 = await convertToBase64(file);
              newFile.base64 = base64;
              newFile.status = "uploaded" as const;
              newFile.progress = 100;
            } catch (error) {
              console.error(
                `Error converting file ${file.name} to base64:`,
                error
              );
              invalidFiles.push(`${file.name} (conversion error)`);
              continue;
            }

            newFiles.push(newFile);
          } else {
            // For images, use secure magic bytes validation
            try {
              const validation = await validateImageFile(file, MAX_FILE_SIZE);

              if (!validation.isValid || !validation.type) {
                invalidFiles.push(
                  `${file.name} (${validation.error || "invalid image format"})`
                );
                continue;
              }

              // Valid image detected - create file object
              const newFile: UploadedFile = {
                id: fileId,
                name: file.name,
                progress: 0,
                status: "uploading" as const,
                file: file,
              };

              // Convert validated image to base64
              try {
                const base64 = await convertToBase64(file);
                newFile.base64 = base64;
                newFile.status = "uploaded" as const;
                newFile.progress = 100;
              } catch (error) {
                console.error(
                  `Error converting file ${file.name} to base64:`,
                  error
                );
                invalidFiles.push(`${file.name} (conversion error)`);
                continue;
              }

              newFiles.push(newFile);
            } catch (error) {
              console.error(`Error validating image ${file.name}:`, error);
              invalidFiles.push(`${file.name} (validation error)`);
              continue;
            }
          }
        }

        // Show error for invalid files
        if (invalidFiles.length > 0) {
          dispatch(
            setStatusPopup({
              status: "error",
              message: `Invalid file(s): ${invalidFiles.join(", ")}. Please select valid image (PNG, JPEG, WEBP, GIF) or PDF files (max 10MB).`,
            })
          );
        }

        // Only proceed with valid files
        if (newFiles.length > 0) {
          handleUploadBulkDocuments(newFiles);
        }
      };

      processFiles();
    }
  };

  const handleUploadBulkDocuments = async (filesValue: UploadedFile[]) => {
    try {
      setIsUploading(true);

      const arrayOfObj: any = [];
      filesValue.forEach((file: any) => {
        arrayOfObj.push({
          fileName: file?.name,
          fileFormat: file?.file?.type?.split?.("/")?.[1],
          base64Data: file?.base64,
        });
      });

      const payload = {
        trackingId: submissionTrackingId,
        userType: tabIndex === 0 ? "applicant" : `coapplicant${tabIndex}`,
        base64Docs: arrayOfObj,
      };

      const response = await eligibilityCheckServices.uploadBulkDocuments(
        payload
      );
      if (response?.status == 200) {
        // handleDocCount(submissionTrackingId);
        response?.data?.successes.forEach((file: any) => {
          file.id = file?.id;
          file.status = "uploaded";
          file.progress = 100;
        });
        setFiles([...files, ...response?.data?.successes]);

        if (response?.data?.failures?.length > 0) {
          dispatch(
            setStatusPopup({
              status: "error",
              message: response?.data?.failures?.[0]?.reason,
            })
          );
        } else {
          setSnackbar({
            open: true,
            message: "Documents uploaded successfully",
            severity: "success",
          });
        }
      }
    } catch (error: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: error?.response?.data?.message,
        })
      );
      console.log(error, "error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    try {
      const payload = {
        deleteType: "bulkDoc",
        docId: [Number(docId)],
        trackingId: submissionTrackingId,
      };
      const response = await eligibilityCheckServices.deleteUserDocument(
        payload
      );
      if (response?.status == 200) {
        setFiles((prev) => prev.filter((file) => file.id !== docId));
      }
    } catch (error: any) {
      console.log(error, "error");
    }
  };

  const handlePreviewDocument = async (docPath: string) => {
    const payload = {
      key: docPath,
    };
    const response = await eligibilityCheckServices.previewDocument(payload);
    if (response?.status == 200) {
      setViewDocUrl(response?.data?.data?.downloadUrl);
      setIsModalOpen(true);
    }
  };

  const handleShowImageMList = () => {
    return (
      <>
        <div>
          <Grid container spacing={2} sx={{ paddingBottom: "20px" }}>
            <Grid item xs={12}>
              <div
                className="image-container"
                style={{
                  border: "1px solid #ddd",
                  padding: "10px",
                  minWidth: isXsOrSm ? "300px" : "100%",
                  height: isXsOrSm ? "320px" : "550px",
                  overflow: "auto",
                  marginTop: "39px",
                }}
              >
                {viewDocUrl?.includes("pdf") ? (
                  <PDFViewer pdfUrl={viewDocUrl} partnerStartedJourney={partnerStartedJourney} />
                ) : (
                  <img src={viewDocUrl} alt="sample" />
                )}
              </div>
            </Grid>
          </Grid>
        </div>
      </>
    );
  };

  const allUploaded =
    files.length > 0 && files.every((f) => f.status === "uploaded");

  return (
    <Box>
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        multiple
        accept={FILE_INPUT_ACCEPT}
        style={{ display: "none" }}
      />

      {/* Dropzone Area */}
      <Box
        onClick={handleDropzoneClick}
        sx={{
          border: "2px dashed #e0e0e0", // Lighter dashed border
          borderRadius: "8px", // More rounded corners
          p: 2, // Padding
          textAlign: "center",
          cursor: "pointer",
          mb: 3, // Margin bottom
          bgcolor: "#f9f9f9", // Light background color
          "&:hover": {
            backgroundColor: "#e0f7fa", // Light teal on hover
            borderColor: "#00b3b5", // Teal border on hover
          },
        }}
      >
        {/* <CloudUploadIcon sx={{ fontSize: 48, color: "#00b3b5", mb: 1 }} />{" "} */}
        <img
          src={uploadIcon}
          alt="upload"
          style={{ width: "24px", marginBottom: "2px" }}
        />
        {/* Larger icon */}
        <Typography variant="body1" sx={{ fontWeight: "bold", color: "#333" }}>
          Drop files here or click to upload
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", fontSize: "0.85rem" }}
        >
          Supported formats: JPG, JPEG, PNG, PDF (max 10MB)
        </Typography>
      </Box>

      {/* Uploaded Files List */}
      <List sx={{ p: 0 }}>
        {isUploading ? (
          <>
            {/* Skeleton for loading state */}
            {[1].map((index) => (
              <ListItem
                key={`skeleton-${index}`}
                sx={{
                  border: "1px solid #eee",
                  mb: 1,
                  borderRadius: "8px",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  {/* File name skeleton */}
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={24}
                    sx={{ bgcolor: "#f0f0f0" }}
                  />

                  {/* Action buttons skeleton */}
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Skeleton
                      variant="rectangular"
                      width={60}
                      height={32}
                      sx={{ borderRadius: "4px", bgcolor: "#f0f0f0" }}
                    />
                    <Skeleton
                      variant="rectangular"
                      width={60}
                      height={32}
                      sx={{ borderRadius: "4px", bgcolor: "#f0f0f0" }}
                    />
                  </Box>
                </Box>

                {/* Progress bar skeleton */}
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={8}
                  sx={{ borderRadius: "4px", bgcolor: "#f0f0f0", mb: 0.5 }}
                />

                {/* Progress percentage skeleton */}
                <Skeleton
                  variant="text"
                  width="10%"
                  height={16}
                  sx={{ bgcolor: "#f0f0f0" }}
                />
              </ListItem>
            ))}
          </>
        ) : (
          <>
            {files?.map((file: any) => (
              <ListItem
                key={file.id}
                sx={{
                  border: "1px solid #eee", // Lighter border
                  mb: 1, // Margin bottom
                  borderRadius: "8px", // More rounded corners
                  p: 1, // Padding
                  display: "flex",
                  flexDirection: "column", // Stack content vertically
                  alignItems: "flex-start", // Align content to start
                  bgcolor: file.progress === 100 ? "#edfdfdff" : "white",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <TruncatedTextWithTooltip
                    text={String(file?.filename || "")}
                    limit={isXsOrSm ? 25 : 35}
                    style={{
                      fontWeight: 500,
                      color: "#333",
                    }}
                  />

                  <ListItemSecondaryAction
                    sx={{
                      position: "static",
                      transform: "none",
                      display: "flex",
                      gap: isXsOrSm ? 0 : 1,
                    }}
                  >
                    <Button
                      size="small"
                      variant="text" // Text variant
                      startIcon={<VisibilityIcon sx={{ fontSize: 18 }} />} // Smaller icon
                      disabled={file.status !== "uploaded"}
                      onClick={() => handlePreviewDocument(file?.s3Url)}
                      sx={{
                        textTransform: "none",
                        color: "#00b3b5",
                        minWidth: "auto",
                        px: 1,
                      }}
                    >
                      {isXsOrSm ? "" : "View"}
                    </Button>
                    <Button
                      color="error"
                      size="small"
                      variant="text" // Text variant
                      startIcon={<DeleteIcon sx={{ fontSize: 18 }} />} // Smaller icon
                      onClick={() => handleDeleteDocument(file.id)}
                      sx={{
                        textTransform: "none",
                        color: "#ea5455",
                        minWidth: "auto",
                      }}
                    >
                      {isXsOrSm ? "" : "Delete"}
                    </Button>
                  </ListItemSecondaryAction>
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <LinearProgress
                    variant="determinate"
                    value={file.progress}
                    sx={{
                      width: "100%",
                      height: 8, // Thicker progress bar
                      borderRadius: 4, // Rounded progress bar
                      bgcolor: "#e0e0e0", // Light grey track
                      "& .MuiLinearProgress-bar": {
                        bgcolor: "#00b3b5", // Teal progress color
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {file.progress}%
                  </Typography>
                </Box>
              </ListItem>
            ))}
          </>
        )}
      </List>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: isXsOrSm ? "space-between" : "center",
          gap: 2,
          mt: 3,
        }}
      >
        <Button
          onClick={onCancel}
          variant="outlined"
          sx={{
            width: isXsOrSm ? "100%" : "auto",
            textTransform: "none", // No uppercase
            borderColor: "#00b3b5", // Teal border
            color: "#00b3b5", // Teal text
            px: 4, // Horizontal padding
            py: 1, // Vertical padding
            borderRadius: "8px", // Rounded corners
            "&:hover": {
              borderColor: "#00999a",
              bgcolor: "#e0f7fa",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => onContinue(files)}
          disabled={!allUploaded || isUploading}
          variant="contained"
          sx={{
            width: isXsOrSm ? "100%" : "auto",
            textTransform: "none",
            bgcolor: "#00b3b5",
            color: "#fff",
            px: 4,
            py: 1,
            borderRadius: "8px",
            "&:hover": {
              bgcolor: "#00999a",
            },
          }}
        >
          Continue
        </Button>
      </Box>

      <Modal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isXsOrSm ? "300px" : "80%",
            minHeight: isXsOrSm ? "380px" : "90%",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          <Close
            onClick={() => {
              setIsModalOpen(false);
            }}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
            }}
          />
          {handleShowImageMList()}
        </Box>
      </Modal>
    </Box>
  );
};

export default BulkUploadModalContent;
